import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { playerCategoryService, Category } from '@/features/game/services/categoryService';
import { playerLifelineService, LifelineDto } from '@/features/game/services/lifelineService';
import { useSubscriptionStore } from '@/features/game/store/subscriptionStore';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { PurchaseModal } from '@/components/shared/PurchaseModal';
import { 
  Plus, Gamepad2, 
  ChevronLeft, ChevronRight, X,
  Sparkles, ShoppingCart
} from 'lucide-react';

import { API_BASE_URL } from '@/lib/api/apiClient';
const API_URL = API_BASE_URL || '';
const ruqaaFont = { fontFamily: "'Aref Ruqaa', 'Amiri', serif" };
const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };

export const PlayerHomePage = () => {
  const navigate = useNavigate();
  const { status: subscriptionStatus, fetchStatus } = useSubscriptionStore();
  const [categories, setCategories] = useState<Category[]>([]);
  const [latestCategories, setLatestCategories] = useState<Category[]>([]);
  const [lifelines, setLifelines] = useState<LifelineDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewCategoriesModal, setShowNewCategoriesModal] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [categorySlide, setCategorySlide] = useState(0);

  useEffect(() => {
    loadData();
    fetchStatus();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load categories, latest (last 3 added), and lifelines in parallel
      const [categoriesData, latestData, lifelinesData] = await Promise.all([
        playerCategoryService.getCategories(),
        playerCategoryService.getLatestCategories(3),
        playerLifelineService.getEnabledLifelines()
      ]);
      setCategories(categoriesData);
      setLatestCategories(latestData);
      setLifelines(lifelinesData);
    } catch (error) {
      console.error('Failed to load data:', error);
      setCategories(playerCategoryService.getFallbackCategories());
      setLatestCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Check subscription status
  const canPlayFree = subscriptionStatus?.canPlayFree ?? true;
  const hasUsedFreeTrial = subscriptionStatus?.hasUsedFreeTrial ?? false;
  const gamesRemaining = subscriptionStatus?.gamesRemaining ?? 0;

  // Handle create game click
  const handleCreateGame = () => {
    // If free trial used and no games, show purchase modal
    if (hasUsedFreeTrial && gamesRemaining <= 0) {
      setShowPurchaseModal(true);
      return;
    }
    navigate('/player/game-settings');
  };

  // آخر 3 فئات مضافة من الأدمن - تتحدث تلقائياً من قاعدة البيانات
  const newCategories = latestCategories.length > 0 ? latestCategories : categories.slice(0, 3);
  const featuredCategories = categories.slice(0, 6);

  // Helper function to get usage text in Arabic
  const getUsageText = (usageTime: string) => {
    if (usageTime === 'BEFORE_QUESTION') {
      return 'تستخدمها قبل ما تشوف السؤال';
    }
    return 'تستخدمها بعد ما تشوف السؤال';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-white" style={arabicFont}>
      {/* Global animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.3); }
          50% { box-shadow: 0 0 40px rgba(34, 197, 94, 0.6); }
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-500 to-green-400 text-white overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 py-16 relative z-10">
          <div className="text-center space-y-6">
            {/* Logo */}
            <div className="flex justify-center">
              <img 
                src={API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg'} 
                alt="اعصر مخك" 
                className="w-40 h-40 md:w-52 md:h-52 lg:w-60 lg:h-60 object-contain"
                style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))' }}
              />
            </div>
            
            {/* Title */}
            <h1 className="text-6xl md:text-8xl" style={{ ...ruqaaFont, fontWeight: 700, textShadow: '4px 4px 8px rgba(0,0,0,0.3)' }}>
              اعصر مخك
            </h1>
            
            {/* Tagline */}
            <p className="text-2xl md:text-3xl text-white/90">
              السؤال علينا والجواب عليك
            </p>
            
            {/* Stats */}
            <p className="text-lg text-white/80">
              ست فئات، ستة وثلاثون سؤالاً، وثلاث وسائل مساعدة
            </p>

            {/* Subscription Status Banner */}
            {subscriptionStatus && (
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-3 inline-block">
                {canPlayFree ? (
                  <p className="text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-300" />
                    <span>لديك تجربة مجانية واحدة</span>
                  </p>
                ) : gamesRemaining > 0 ? (
                  <p className="text-white flex items-center gap-2">
                    <Gamepad2 className="w-5 h-5 text-yellow-300" />
                    <span>لديك <strong className="text-yellow-300">{gamesRemaining}</strong> ألعاب متبقية</span>
                  </p>
                ) : (
                  <p className="text-white flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-yellow-300" />
                    <span>اشترِ ألعاب للمتابعة!</span>
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-4 pt-8">
              {/* Create Game - Main CTA */}
              <button
                onClick={handleCreateGame}
                className="bg-white text-green-600 hover:bg-yellow-300 hover:text-green-700 px-8 py-4 rounded-2xl font-bold text-xl shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 flex items-center gap-3"
              >
                <Plus className="w-6 h-6" />
                {canPlayFree ? 'ابدأ التجربة المجانية' : (gamesRemaining > 0 ? 'إنشاء لعبة' : 'اشترِ والعب')}
              </button>
              
              {/* Buy Games */}
              <button 
                onClick={() => setShowPurchaseModal(true)}
                className="bg-gray-800 text-white hover:bg-gray-900 px-6 py-4 rounded-2xl font-bold text-lg shadow-lg transition-all flex items-center gap-2"
              >
                <ShoppingCart className="w-5 h-5 text-yellow-400" />
                اشترِ ألعاب
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4" style={ruqaaFont}>
            اعصر مخك
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            مسابقة أسئلة للجميع مع عشرات الفئات وأقسام للكبار والصغار
          </p>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Video Player */}
            <div className="relative aspect-video bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="شرح لعبة اعصر مخك"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            
            {/* CTA Button */}
            <div className="text-center mt-8">
              <button
                onClick={() => navigate('/player/game-settings')}
                className="bg-green-600 hover:bg-green-700 text-white px-10 py-4 rounded-full font-bold text-xl shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-3"
              >
                <Plus className="w-6 h-6" />
                اشتر باقة والعب
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Explanation Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4" style={ruqaaFont}>شرح الفئات</h2>
            <p className="text-xl text-gray-600">بعض الفئات في اللعبة لها طريقة لعب مختلفة، شوفوا طريقتها قبل لا تلعبونها</p>
          </div>
          
          {/* Categories Carousel */}
          <div className="relative max-w-5xl mx-auto">
            <div className="flex items-center justify-center gap-6">
              {/* Prev Button */}
              <button 
                onClick={() => setCategorySlide(Math.max(0, categorySlide - 1))}
                className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all transform hover:scale-110"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
              
              {/* Category Cards */}
              <div className="flex gap-6 overflow-hidden">
                {featuredCategories.slice(categorySlide, categorySlide + 3).map((cat, index) => (
                  <div 
                    key={cat.id}
                    className={`bg-white rounded-3xl shadow-xl overflow-hidden transition-all duration-300 ${
                      index === 1 ? 'scale-110 z-10 ring-4 ring-green-400 shadow-green-500/30' : 'scale-95 opacity-80'
                    }`}
                    style={{ width: '200px' }}
                  >
                    <div className={`${index === 1 ? 'bg-gradient-to-b from-green-400 to-green-500' : 'bg-gray-100'} p-4`}>
                      <p className={`text-center font-bold ${index === 1 ? 'text-white' : 'text-gray-600'}`}>
                        {cat.name}
                      </p>
                    </div>
                    <div className="p-4">
                      <div className="aspect-square bg-gray-200 rounded-2xl overflow-hidden mb-4">
                        <img 
                          src={cat.imageUrl ? (API_URL ? `${API_URL}${cat.imageUrl.startsWith('/') ? cat.imageUrl : '/' + cat.imageUrl}` : (cat.imageUrl.startsWith('/') ? cat.imageUrl : '/' + cat.imageUrl)) : (API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg')}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.src = API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg'; }}
                        />
                      </div>
                      <button className={`w-full py-3 rounded-xl font-bold transition-all ${
                        index === 1 
                          ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg' 
                          : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                      }`}>
                        ابدأ الشرح
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Next Button */}
              <button 
                onClick={() => setCategorySlide(Math.min(featuredCategories.length - 3, categorySlide + 1))}
                className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all transform hover:scale-110"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Lifelines Section - From Database */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12" style={ruqaaFont}>
            وسائل المساعدة
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {lifelines.map((lifeline) => (
              <div key={lifeline.id} className="bg-white rounded-2xl shadow-lg p-6 text-center">
                <h3 className="text-xl font-bold text-gray-800 mb-4">{lifeline.nameAr}</h3>
                
                {/* Icon/Image */}
                <div 
                  className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 overflow-hidden"
                  style={{ 
                    backgroundColor: lifeline.iconColor ? `${lifeline.iconColor}20` : '#E5E7EB', 
                    border: `3px solid ${lifeline.iconColor || '#9CA3AF'}` 
                  }}
                >
                  {lifeline.iconUrl ? (
                    <img 
                      src={API_URL ? `${API_URL}${lifeline.iconUrl}` : (lifeline.iconUrl?.startsWith('/') ? lifeline.iconUrl : `/${lifeline.iconUrl || ''}`)}
                      alt={lifeline.nameAr}
                      className="w-12 h-12 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = `<span style="font-size: 2rem; color: ${lifeline.iconColor || '#9CA3AF'}">⭐</span>`;
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '2rem', color: lifeline.iconColor || '#9CA3AF' }}>⭐</span>
                  )}
                </div>
                
                {/* Description */}
                <p className="text-gray-600 text-sm mb-6 min-h-[60px]">
                  {lifeline.descriptionAr}
                </p>
                
                {/* Usage Button */}
                <button 
                  className={`w-full py-3 rounded-xl font-bold text-white text-sm transition-all ${
                    lifeline.usageTime === 'BEFORE_QUESTION'
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 shadow-md hover:shadow-lg' 
                      : 'bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900'
                  }`}
                >
                  {getUsageText(lifeline.usageTime)}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-12 max-w-5xl mx-auto">
            {/* Device Mockups */}
            <div className="relative">
              <div className="w-80 h-60 bg-gray-200 rounded-2xl shadow-xl flex items-center justify-center">
                <Gamepad2 className="w-20 h-20 text-gray-400" />
              </div>
            </div>
            
            {/* Content */}
            <div className="text-center md:text-right">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4" style={ruqaaFont}>
                جاهزين تلعبون؟
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                لعبتك الأولى علينا، جربها الحين
              </p>
              <button 
                onClick={() => navigate('/player/game-settings')}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-12 py-5 rounded-full font-bold text-2xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              >
                إنشاء لعبة
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Social Media */}
            <div className="flex items-center gap-4">
              <a href="#" className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/></svg>
              </a>
              <a href="#" className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
              </a>
              <a href="#" className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="#" className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>

            {/* Copyright */}
            <p className="text-gray-500 text-sm">
              Copyright 2026 اعصر مخك - All Rights Reserved ©
            </p>

            {/* App Store Links */}
            <div className="flex items-center gap-4">
              <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/></svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* New Categories Modal */}
      {showNewCategoriesModal && newCategories.length > 0 && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-green-500 via-emerald-600 to-teal-700 rounded-3xl p-8 max-w-2xl w-full relative overflow-hidden shadow-2xl">
            {/* Close Button */}
            <button 
              onClick={() => setShowNewCategoriesModal(false)}
              className="absolute top-4 left-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors z-20"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-yellow-300 rounded-full blur-3xl opacity-40" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-300 rounded-full blur-3xl opacity-30" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-lime-400 rounded-full blur-3xl opacity-20" />
            
            {/* Animated sparkles */}
            <div className="absolute top-8 right-8 text-yellow-300 animate-pulse text-2xl">✦</div>
            <div className="absolute bottom-8 right-16 text-yellow-200 animate-bounce text-xl">✧</div>
            <div className="absolute top-16 left-16 text-lime-200 animate-ping text-lg">★</div>
            
            {/* Content */}
            <div className="relative z-10">
              <div className="text-center mb-8">
                <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-4">
                  <p className="text-white/90 text-lg font-bold">فئات اعصر مخك</p>
                </div>
                <h3 className="text-4xl md:text-5xl font-bold text-white mb-2" style={ruqaaFont}>
                  جديد هذا الأسبوع
                </h3>
                <div className="flex justify-center gap-2 mt-4">
                  <span className="text-3xl animate-bounce" style={{ animationDelay: '0ms' }}>🌟</span>
                  <span className="text-3xl animate-bounce" style={{ animationDelay: '100ms' }}>🧠</span>
                  <span className="text-3xl animate-bounce" style={{ animationDelay: '200ms' }}>🌟</span>
                </div>
              </div>
              
              {/* Category Cards */}
              <div className="flex justify-center gap-5 flex-wrap">
                {newCategories.map((cat, index) => (
                  <div 
                    key={cat.id} 
                    className="bg-white rounded-2xl shadow-2xl overflow-hidden w-44 transform hover:scale-105 transition-all duration-300 hover:shadow-green-500/30"
                    style={{ 
                      animation: `fadeInUp 0.5s ease-out ${index * 0.15}s both`
                    }}
                  >
                    <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 relative">
                      <img 
                        src={cat.imageUrl ? (API_URL ? `${API_URL}${cat.imageUrl.startsWith('/') ? cat.imageUrl : '/' + cat.imageUrl}` : (cat.imageUrl.startsWith('/') ? cat.imageUrl : '/' + cat.imageUrl)) : (API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg')}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.src = API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg'; }}
                      />
                      {/* New badge */}
                      <div className="absolute top-2 right-2 bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-1 rounded-full">
                        جديد
                      </div>
                    </div>
                    <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600">
                      <p className="text-white text-center font-bold text-sm truncate">{cat.name}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* CTA */}
              <div className="text-center mt-8">
                <button 
                  onClick={() => setShowNewCategoriesModal(false)}
                  className="bg-white text-green-600 hover:bg-yellow-300 hover:text-green-700 px-8 py-3 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  اكتشف الفئات الجديدة
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      <PurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        reason={hasUsedFreeTrial && gamesRemaining <= 0 ? 'no_games' : 'free_trial_used'}
        gamesRemaining={gamesRemaining}
      />
    </div>
  );
};
